/**
 * Shared OOXML relationship-resolution helpers.
 *
 * A single-slide `slide.exportAsBase64()` export carries the slide's own
 * `_rels/slideN.xml.rels` alongside it, which is the only way to resolve
 * parts that are linked rather than inline (notesSlide, comments, …) — the
 * PowerPoint JS API has no surface for any of these. Shared by SpeakerNotes
 * (notesSlide read/write) and brand-check (notes-present / comments-present
 * detection) so both resolve relationships the same way.
 */

/** Resolve an OOXML relationship Target (relative to `baseDir`) into a normalized zip-entry path. */
export function resolveRelativePath(baseDir: string, target: string): string {
  const parts = baseDir.split("/").filter(Boolean);
  for (const segment of target.split("/")) {
    if (segment === "" || segment === ".") continue;
    if (segment === "..") parts.pop();
    else parts.push(segment);
  }
  return parts.join("/");
}

/**
 * Resolve the zip-entry path of the first relationship in `relsXml` whose
 * `Type` matches `typeMatch` — an exact suffix (via `endsWith`) or a predicate
 * for schemas that vary across PowerPoint versions (e.g. comments). Returns
 * `null` when `relsXml` is absent, unparsable, or no relationship matches.
 */
export function resolveRelationshipTarget(
  relsXml: string | null,
  typeMatch: string | ((type: string) => boolean),
  baseDir: string,
): string | null {
  if (!relsXml) return null;
  const relsDoc = new DOMParser().parseFromString(relsXml, "text/xml");
  if (relsDoc.getElementsByTagName("parsererror").length > 0) return null;

  const matches = typeof typeMatch === "string" ? (t: string) => t.endsWith(typeMatch) : typeMatch;

  for (const rel of relsDoc.getElementsByTagName("Relationship")) {
    const relType = rel.getAttribute("Type");
    if (!relType || !matches(relType)) continue;
    const target = rel.getAttribute("Target");
    if (!target) continue; // malformed relationship — no Target to resolve
    return resolveRelativePath(baseDir, target);
  }
  return null;
}
